import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Monitor } from 'lucide-react';
import { toast } from 'react-toastify';
import { api } from '../lib_api';
import { Mt5DesktopModal } from '../components/Mt5DesktopModal';

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  failed: 'bg-red-100 text-red-800',
  activated: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-gray-100 text-gray-700',
};

const ORDER = ['pending', 'in_progress', 'failed', 'activated', 'cancelled'];

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return <span className={`rounded px-2 py-1 text-xs font-medium ${color}`}>{status}</span>;
}

export default function Mt5Activations() {
  const queryClient = useQueryClient();
  const [desktopSession, setDesktopSession] = useState<any>(null);
  const requests = useQuery({
    queryKey: ['admin', 'mt5-activations'],
    queryFn: async () => (await api.get('/api/admin/mt5/autotrading-requests')).data,
    refetchInterval: 20000,
  });

  const mutateStatus = useMutation({
    mutationFn: async ({ id, action }: { id: number; action: 'in-progress' | 'activated' | 'failed' | 'recheck' }) => (await api.post(`/api/admin/mt5/autotrading-requests/${id}/${action}`)).data,
    onSuccess: (payload, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'mt5-activations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'mt5-activations-pending-count'] });
      const label = variables.action === 'activated'
        ? 'Algo Trading readiness was confirmed and the account was marked activated.'
        : variables.action === 'recheck'
          ? payload?.readyToMarkActivated
            ? 'MT5 readiness confirmed. Click Mark Activated to complete the request.'
            : 'MT5 is not ready yet. Confirm Algo Trading is enabled, then recheck.'
          : `Activation request marked ${variables.action.replace('-', ' ')}.`;
      toast.success(label);
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'The activation update failed.'),
  });

  const openDesktop = useMutation({
    mutationFn: async (id: number) => (await api.post(`/api/admin/mt5/autotrading-requests/${id}/desktop-session`)).data,
    onSuccess: (payload) => {
      setDesktopSession(payload);
      queryClient.invalidateQueries({ queryKey: ['admin', 'mt5-activations'] });
    },
    onError: (error: any) => toast.error(error?.response?.data?.error || 'The MT5 desktop could not be opened.'),
  });

  const items = useMemo(() => {
    const raw = requests.data?.items || [];
    return [...raw].sort((a: any, b: any) => {
      const left = ORDER.indexOf(a.status);
      const right = ORDER.indexOf(b.status);
      if (left !== right) return left - right;
      return new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime();
    });
  }, [requests.data?.items]);

  return (
    <div className="space-y-4">
      <section className="panel p-4">
        <h3 className="text-base font-semibold">Admin guidance</h3>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-mutedGray dark:text-darkTextSecondary">
          <li>Click Open MT5 Desktop for the requested account.</li>
          <li>Enter the private VNC PIN in the secure dashboard prompt.</li>
          <li>Confirm MT5 is logged in.</li>
          <li>Tools → Options → Expert Advisors → Allow algorithmic trading.</li>
          <li>Turn on Algo Trading toolbar button.</li>
          <li>Click Recheck Readiness.</li>
          <li>Mark Activated.</li>
        </ol>
      </section>

      <section className="panel overflow-x-auto p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold">AutoTrading Activation Requests</h3>
          <span className="text-sm text-mutedGray">Pending: {requests.data?.pendingCount || 0}</span>
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left dark:bg-white/5">
            <tr>
              <th className="px-2 py-2">Status</th><th className="px-2 py-2">User</th><th className="px-2 py-2">Account</th><th className="px-2 py-2">Broker</th><th className="px-2 py-2">Login</th><th className="px-2 py-2">Server</th><th className="px-2 py-2">Runtime</th><th className="px-2 py-2">Connection</th><th className="px-2 py-2">Last error</th><th className="px-2 py-2">Created</th><th className="px-2 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row: any) => (
              <tr key={row.id} className="border-t border-gray-100 align-top dark:border-darkCard">
                <td className="px-2 py-2"><StatusBadge status={row.status} /></td>
                <td className="px-2 py-2">{row.userName || row.userEmail}<div className="text-xs text-mutedGray">{row.userEmail}</div></td>
                <td className="px-2 py-2">{row.tradingAccountId}</td>
                <td className="px-2 py-2">{row.brokerName || '-'}</td>
                <td className="px-2 py-2">{row.mt5LoginMasked || '-'}</td>
                <td className="px-2 py-2">{row.mt5Server || '-'}<div><button className="text-xs text-indigo-600" onClick={() => navigator.clipboard.writeText(row.mt5Server || '')}>Copy</button></div></td>
                <td className="px-2 py-2">{row.runtimeTerminalDir || '-'}<div><button className="text-xs text-indigo-600" onClick={() => navigator.clipboard.writeText(row.runtimeTerminalDir || '')}>Copy</button></div></td>
                <td className="px-2 py-2">{row.connectionStatus || '-'}</td>
                <td className="px-2 py-2">{row.lastError || '-'}</td>
                <td className="px-2 py-2">{row.requestedAt ? new Date(row.requestedAt).toLocaleString() : '-'}</td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap gap-1">
                    <button disabled={openDesktop.isPending || !['connected', 'worker_started'].includes(row.connectionStatus)} className="btn-primary !px-2 !py-1 text-xs" onClick={() => openDesktop.mutate(row.id)}>
                      <span className="inline-flex items-center gap-1"><Monitor size={14} />{openDesktop.isPending && openDesktop.variables === row.id ? 'Opening...' : 'Open MT5 Desktop'}</span>
                    </button>
                    <button disabled={mutateStatus.isPending || row.status === 'activated'} className="btn-outline !px-2 !py-1 text-xs" onClick={() => mutateStatus.mutate({ id: row.id, action: 'in-progress' })}>Mark In Progress</button>
                    <button disabled={mutateStatus.isPending} className="btn-outline !px-2 !py-1 text-xs" onClick={() => mutateStatus.mutate({ id: row.id, action: 'recheck' })}>Recheck Readiness</button>
                    <button disabled={mutateStatus.isPending || row.status === 'activated'} className="btn-outline !px-2 !py-1 text-xs" onClick={() => mutateStatus.mutate({ id: row.id, action: 'activated' })}>{mutateStatus.isPending && mutateStatus.variables?.id === row.id && mutateStatus.variables?.action === 'activated' ? 'Activating...' : row.status === 'activated' ? 'Activated' : 'Mark Activated'}</button>
                    <button disabled={mutateStatus.isPending || row.status === 'activated'} className="btn-outline !px-2 !py-1 text-xs" onClick={() => mutateStatus.mutate({ id: row.id, action: 'failed' })}>Mark Failed</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      {desktopSession && <Mt5DesktopModal session={desktopSession} onClose={() => setDesktopSession(null)} />}
    </div>
  );
}
